import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Recurso } from '../../api/ops/pagos';
import type { FotoSala, ReservaEnVivo, VersionPlano } from '../../api/ops/sala';
import { Icono } from '../../componentes/Icono';
import { nombreDe, type ContextoReservas } from '../reservas/contexto';
import { diaLargo, horaEn, sumarDias } from '../reservas/fechas';
import { mayuscula } from '../reservas/textos';
import { sigueViva } from './logica';

/**
 * «Imprimir la ocupación» de la Sala (8.7, la RV Hoja imprimible de 8.1): el día (Hoy, Mañana u otra fecha), las salas
 * que se quieren, las cifras del día (reservas, personas, recursos con reserva, sin recurso, ya llegaron, sin pagar) y,
 * por sala, sus reservas con la casilla de la puerta. Al abrirse deja fuera de la impresión todo lo demás (`rv-imprime`).
 * Llega también desde «Imprimir» de 8.1 con `/sala?imprimir=1&dia=` (G796).
 */
interface Props {
    dia: string;
    hoy: string;
    foto: FotoSala;
    version: VersionPlano;
    recursos: readonly Recurso[];
    nombres: Readonly<Record<string, string>>;
    contexto: ContextoReservas;
    onDia: (dia: string) => void;
    onCerrar: () => void;
}

export function HojaOcupacion({ dia, hoy, foto, version, recursos, nombres, contexto, onDia, onCerrar }: Props) {
    const capa = useRef<HTMLDivElement>(null);
    const salas = [...version.content.rooms].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const [fuera, setFuera] = useState<ReadonlySet<string>>(new Set());

    useEffect(() => {
        const raiz = document.documentElement;
        raiz.classList.add('rv-imprime');
        const resto = [...document.body.children].filter((e) => e !== capa.current);
        for (const e of resto) {
            e.setAttribute('data-rv-hoja-fuera', '');
        }
        const tecla = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onCerrar();
            }
        };
        window.addEventListener('keydown', tecla);
        return () => {
            raiz.classList.remove('rv-imprime');
            for (const e of resto) {
                e.removeAttribute('data-rv-hoja-fuera');
            }
            window.removeEventListener('keydown', tecla);
        };
    }, [onCerrar]);

    const salaDe = (r: ReservaEnVivo): string | null => {
        const id = (r.resource_ids ?? [])[0];
        const ref = version.content.positions.find((p) => p.resource_id === id)?.room_ref;
        return salas.find((s) => s.room_id === ref || s.name === ref)?.name ?? null;
    };
    const vivas = foto.reservations.filter(sigueViva).sort((a, b) => a.starts_at.localeCompare(b.starts_at));
    const conRecurso = new Set(vivas.flatMap((r) => r.resource_ids ?? []));
    const enPlano = version.content.positions.length;
    const cifras = [
        ['Reservas', String(vivas.length)],
        ['Personas', String(vivas.reduce((n, r) => n + r.party_size, 0))],
        [`${mayuscula(contexto.palabra)}s con reserva`, `${String(conRecurso.size)} / ${String(enPlano)}`],
        [`Sin ${contexto.palabra}`, String(vivas.filter((r) => (r.resource_ids ?? []).length === 0).length)],
        ['Ya llegaron', String(vivas.filter((r) => r.status === 'arrived').length)],
        ['Sin pagar', String(vivas.filter((r) => r.status === 'pending_payment').length)],
    ] as const;
    const grupos = [
        ...salas.map((s) => ({ nombre: s.name, filas: vivas.filter((r) => salaDe(r) === s.name) })),
        { nombre: `Sin ${contexto.palabra}`, filas: vivas.filter((r) => salaDe(r) === null) },
    ].filter((g) => !fuera.has(g.nombre) && g.filas.length > 0);
    const codigo = (r: ReservaEnVivo) => (r.resource_ids ?? []).map((id) => recursos.find((x) => x.id === id)?.code ?? '').filter((x) => x !== '').join(' + ');
    const ahora = new Date();

    return createPortal(
        <div ref={capa} className="rv-hoja-fondo" role="dialog" aria-modal="true" aria-label="Ocupación de sala para imprimir">
            <div className="rv-hoja-barra">
                <span className="rv-hoja-barra-t">Imprimir la ocupación</span>
                <button type="button" className="rv-b rv-b-sec" onClick={onCerrar}>
                    <Icono nombre="arrow-left" />
                    <span>Volver a Sala</span>
                </button>
                <button type="button" className="rv-b rv-b-pri" onClick={() => { window.print(); }}>
                    <Icono nombre="printer" />
                    <span>Imprimir o guardar en PDF</span>
                </button>
            </div>
            <div className="rv-sl-hoja-opciones" data-rv-no-imprimir="">
                <div role="group" aria-label="Día">
                    <button type="button" className="rv-b rv-b-sec" aria-pressed={dia === hoy} onClick={() => { onDia(hoy); }}>Hoy</button>
                    <button type="button" className="rv-b rv-b-sec" aria-pressed={dia === sumarDias(hoy, 1)} onClick={() => { onDia(sumarDias(hoy, 1)); }}>Mañana</button>
                    <label className="rv-sl-hoja-fecha">
                        <span>Otra fecha</span>
                        <input type="date" value={dia} onChange={(e) => { if (e.target.value !== '') { onDia(e.target.value); } }} />
                    </label>
                </div>
                <div role="group" aria-label="Salas">
                    {salas.map((s) => (
                        <label key={s.name} className="rv-sl-hoja-sala">
                            <input type="checkbox" checked={!fuera.has(s.name)} onChange={() => { setFuera((f) => { const n = new Set(f); if (n.has(s.name)) { n.delete(s.name); } else { n.add(s.name); } return n; }); }} />
                            <span>{s.name}</span>
                        </label>
                    ))}
                </div>
            </div>
            <article className="rv-hoja" aria-label={`Ocupación de sala, ${diaLargo(dia)}`}>
                <header className="rv-hoja-cab">
                    <div>
                        <h1 className="rv-hoja-titulo">Ocupación de sala</h1>
                        <span className="rv-hoja-sub">{mayuscula(diaLargo(dia))}</span>
                    </div>
                    <div className="rv-hoja-negocio"><b>{contexto.nombreNegocio}</b></div>
                </header>
                <div className="rv-sl-hoja-cifras">
                    {cifras.map(([t, n]) => (
                        <div key={t} role="group" aria-label={t}>
                            <small>{t.toLocaleUpperCase('es')}</small>
                            <b>{n}</b>
                        </div>
                    ))}
                </div>
                {grupos.length === 0 && <p className="rv-hoja-vacio">No hay reservas este día.</p>}
                {grupos.map((g) => (
                    <section key={g.nombre} className="rv-hoja-turno" aria-label={g.nombre}>
                        <div className="rv-hoja-banda">
                            <div><span className="rv-hoja-banda-t">{g.nombre}</span></div>
                            <div className="rv-hoja-banda-n">
                                <span><b>{g.filas.length}</b>{g.filas.length === 1 ? ' reserva' : ' reservas'}</span>
                                <span><b>{g.filas.reduce((n, r) => n + r.party_size, 0)}</b> personas</span>
                            </div>
                        </div>
                        <table className="rv-hoja-tabla">
                            <thead>
                                <tr>
                                    <th scope="col" className="rv-hoja-hora">Hora</th>
                                    <th scope="col" className="rv-hoja-mesa">{mayuscula(contexto.palabra)}</th>
                                    <th scope="col" className="rv-hoja-cli">Cliente</th>
                                    <th scope="col" className="rv-hoja-pax">Pax</th>
                                    <th scope="col" className="rv-hoja-puerta">Puerta</th>
                                </tr>
                            </thead>
                            <tbody>
                                {g.filas.map((r) => (
                                    <tr key={r.reservation_id}>
                                        <td className="rv-hoja-hora">{horaEn(r.starts_at, contexto.zona)}</td>
                                        <td className="rv-hoja-mesa">{codigo(r) || '—'}</td>
                                        <td className="rv-hoja-cli"><b>{nombreDe(r, nombres)}</b></td>
                                        <td className="rv-hoja-pax">{r.party_size}</td>
                                        <td className="rv-hoja-puerta">
                                            {r.status === 'arrived' ? <span className="rv-hoja-chip">Llegó</span> : <span className="rv-hoja-casilla" role="img" aria-label="Sin marcar: casilla para marcar a mano" />}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                ))}
                <footer className="rv-hoja-pie">
                    <span>{`${contexto.nombreNegocio} · ${mayuscula(diaLargo(dia))}`}</span>
                    <span>{`Impreso el ${diaLargo(hoy)} a las ${horaEn(ahora, contexto.zona)}`}</span>
                </footer>
            </article>
        </div>,
        document.body,
    );
}
